import { AfterViewInit, Component, DestroyRef, ElementRef, EventEmitter, Input, Output, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { fromEvent } from 'rxjs';
import { Computer } from 'src/app/models/computer';
import { LanguageService } from 'src/app/services/account/language.service';
import { NavigationPlanRowComponent } from '../navigation-plan-row/navigation-plan-row.component';
import { BatteryComponent } from '../../navigation-hud/battery/battery.component';

@Component({
  standalone: true,
  selector: 'app-navigation-plan-normal-state',
  templateUrl: './navigation-plan-normal-state.component.html',
  styleUrls: ['./navigation-plan-normal-state.component.scss'],
  imports: [CommonModule, NavigationPlanRowComponent, BatteryComponent]
})
export class NavigationPlanNormalStateComponent implements AfterViewInit {
  @ViewChild('pagerStrip', { static: false }) pagerStrip!: ElementRef<HTMLElement>;

  @Input() estimatedBatteryLevel: number = 100;
  @Input() cannotSkipMoreChargingStation: boolean = false;

  // Setter so a shrinking array (a waypoint was passed / skipped) pulls the
  // active page back into range instead of leaving the pager on a blank page.
  @Input() set computersArray(value: Computer[]) {
    this._computersArray = value ?? [];
    if (this.activeIndex() > this.maxIndex) {
      this.activeIndex.set(this.maxIndex);
    }
  }
  get computersArray(): Computer[] {
    return this._computersArray;
  }
  private _computersArray: Computer[] = [];

  @Output() skipWaypointClick = new EventEmitter<void>();
  @Output() skipChargingStationClick = new EventEmitter<void>();
  @Output() stopNavigationClick = new EventEmitter<void>();
  @Output() batteryReservedWarningClick = new EventEmitter<void>();

  /** Which page (computer) is shown: 0 = next waypoint/charging station when present, last = destination. */
  public readonly activeIndex = signal<number>(0);
  public readonly isSwiping = signal<boolean>(false);

  private readonly destroyRef = inject(DestroyRef);

  constructor(public languageService: LanguageService, private elementRef: ElementRef<HTMLElement>) {}

  public get pageCount(): number {
    return Math.max(1, this._computersArray.length);
  }

  private get maxIndex(): number {
    return this.pageCount - 1;
  }

  public get activeComputer(): Computer | null {
    return this._computersArray[this.activeIndex()] ?? null;
  }

  /** Skipping applies to waypoint and charging-station pages only. */
  public get skipAvailable(): boolean {
    const type = this.activeComputer?.Type;
    return type == 'wp' || type == 'cs';
  }

  public goTo(index: number): void {
    this.activeIndex.set(Math.min(Math.max(index, 0), this.maxIndex));
  }

  public onSkipClick(): void {
    const type = this.activeComputer?.Type;
    if (type == 'wp') {
      this.skipWaypointClick.emit();
    } else if (type == 'cs') {
      this.skipChargingStationClick.emit();
    }
  }

  public onStopNavigation(): void {
    this.stopNavigationClick.emit();
  }

  public onBatteryReservedWarning(): void {
    this.batteryReservedWarningClick.emit();
  }

  ngAfterViewInit(): void {
    this.initSwipeEvents();
  }

  /** Tap-and-move sideways anywhere on the bar switches between the pages. */
  private initSwipeEvents(): void {
    const host = this.elementRef.nativeElement;

    const isInteractive = (target: EventTarget | null): boolean => {
      return target instanceof Element && !!target.closest('button, a, .battery-reserved-warning-icon');
    };

    let startX = 0;
    let deltaX = 0;
    let active = false;

    const baseTransform = (): string => `translateX(${-this.activeIndex() * 100}%)`;

    fromEvent<PointerEvent>(host, 'pointerdown').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (this.pageCount < 2 || isInteractive(event.target)) {
        return;
      }
      active = true;
      startX = event.clientX;
      deltaX = 0;
      this.isSwiping.set(true);
      host.setPointerCapture?.(event.pointerId);
    });

    fromEvent<PointerEvent>(host, 'pointermove').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (!active) {
        return;
      }
      deltaX = event.clientX - startX;
      // Dragging past the first/last page only moves the strip a fraction of
      // the finger travel, so the edge reads as an edge instead of a dead zone.
      const atEdge = (this.activeIndex() == 0 && deltaX > 0) || (this.activeIndex() == this.maxIndex && deltaX < 0);
      const offset = atEdge ? deltaX * 0.3 : deltaX;
      // Written straight to the DOM to keep the gesture smooth — no change
      // detection churn per move event. The bound transform takes over again
      // once the gesture ends.
      this.pagerStrip.nativeElement.style.transform = `translateX(calc(${-this.activeIndex() * 100}% + ${offset}px))`;
    });

    const endSwipe = (): void => {
      if (!active) {
        return;
      }
      active = false;
      this.isSwiping.set(false);

      const threshold = 40;
      if (deltaX < -threshold) {
        this.goTo(this.activeIndex() + 1);
      } else if (deltaX > threshold) {
        this.goTo(this.activeIndex() - 1);
      }
      // A below-threshold drag leaves the bound transform value unchanged, so
      // Angular would skip the write and the strip would stay where the finger
      // left it — always snap the element back explicitly.
      this.pagerStrip.nativeElement.style.transform = baseTransform();
    };

    fromEvent<PointerEvent>(host, 'pointerup').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(endSwipe);
    fromEvent<PointerEvent>(host, 'pointercancel').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(endSwipe);
  }
}
